import { useCallback, useEffect, useMemo, useState } from "react";

import { debounce } from "lodash";

export interface UseContentFitsOptions {
  /**
   * Set to true for editable inputs. Necessary due to an interaction between Chakra Editable and Tooltip.
   * @default false
   */
  skipClientWidthZero?: boolean;
  /**
   * Debounce delay in milliseconds for resize checks.
   * @default 200
   */
  debounceMs?: number;
  /**
   * Which axis to check for overflow.
   * - "horizontal": compares scrollWidth vs clientWidth (for single-line truncation with text-overflow: ellipsis)
   * - "vertical": compares scrollHeight vs clientHeight (for multi-line clamping with -webkit-line-clamp)
   * @default "horizontal"
   */
  axis?: "horizontal" | "vertical";
}

/**
 * Returns true if the element's content fits, false if the content overflows.
 * If the element doesn't exist or is hidden, returns true.
 */
export const useContentFits = <TargetElement extends HTMLElement>(
  enabled = true,
  /**
   * Set to true for editable inputs. Necessary due to an interaction between Chakra Editable and Tooltip.
   */
  {
    skipClientWidthZero = false,
    debounceMs = 200,
    axis = "horizontal",
  }: UseContentFitsOptions = {},
) => {
  const [targetElement, setTargetElement] = useState<TargetElement | null>(
    null,
  );
  const [contentFits, setContentFits] = useState(true);
  const ref = useCallback((node: TargetElement | null) => {
    setTargetElement((prev) => {
      // Ignore null transitions. When Tooltip (via @chakra-ui/react-use-merge-refs) creates
      // a new merged ref on each render, React calls the old ref with null immediately before
      // calling the new ref with the same element. Treating null as a no-op prevents the
      // null → element state cycle from causing an infinite render loop.
      if (node === null) return prev;
      return prev === node ? prev : node;
    });
  }, []);

  const checkContentFits = useCallback(
    (el: TargetElement | null) => {
      if (el) {
        /**
         * This is needed when using Chakra Tooltip with an Editable component child.
         *
         * Bug: editable component will sometimes immediately exit editing state after entering.
         * Cause: Chakra Tooltip, when disabled, completely removes the tooltip components, forcing
         * a remount of the child editable component, which resets the editing state.
         * Hacky fix: Detects and ignores the content fits state when entering editing state,
         * detected by the preview component being hidden.
         */
        if (skipClientWidthZero && el.clientWidth === 0) {
          return;
        }
        if (axis === "vertical") {
          setContentFits(el.scrollHeight <= el.clientHeight);
        } else {
          setContentFits(el.scrollWidth <= el.clientWidth);
        }
      }
    },
    [skipClientWidthZero, axis],
  );

  const debouncedCheckContentFits = useMemo(
    () =>
      debounceMs < 0
        ? () => checkContentFits(targetElement)
        : debounce(() => checkContentFits(targetElement), debounceMs),
    [checkContentFits, targetElement, debounceMs],
  );

  // @ts-expect-error Not all code paths return a value -- expected for conditional useEffect
  useEffect(() => {
    if (enabled) {
      debouncedCheckContentFits();

      if (targetElement) {
        const resizeObserver = new ResizeObserver(debouncedCheckContentFits);
        resizeObserver.observe(targetElement);
        window.addEventListener("resize", debouncedCheckContentFits);

        return () => {
          if (
            "cancel" in debouncedCheckContentFits &&
            typeof debouncedCheckContentFits.cancel === "function"
          ) {
            debouncedCheckContentFits.cancel();
          }
          resizeObserver.disconnect();
          window.removeEventListener("resize", debouncedCheckContentFits);
        };
      }
    } else {
      // If disabled, reset to default state (content fits)
      setContentFits(true);
    }
  }, [enabled, debouncedCheckContentFits, targetElement]);

  return { contentFits, ref };
};
